/**
 * Notifications API — RTK Query base slice
 *
 * Split into three files for maintainability:
 *   - notificationsApi.ts  (this file) — createApi + tag types
 *   - notificationsQueries.ts           — all query endpoints
 *   - notificationsMutations.ts         — all mutation endpoints
 *
 * Pattern: fakeBaseQuery + direct Supabase calls + injectEndpoints.
 */
import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";

// ─── Shared Types (used across queries + mutations) ───────────────────────────

export interface QueueRow {
  id: string;
  school_id: string;
  target_date: string;
  send_at: string;
  initiated_by: string;
  publish_version: number;
  status: string;
  last_error: string | null;
  last_result: Record<string, unknown> | null;
  excluded_teacher_ids: string[] | null;
  created_at: string;
  updated_at: string;
}

export interface NotificationSettings {
  id: string;
  school_id: string;
  send_delay_minutes: number;
}

export interface GetPreferencesArgs {
  profileId: string;
  schoolId: string;
}

export interface DateActionArgs {
  date: string;
}

export interface QueueStatusArgs {
  schoolId: string;
  date: string;
}

export interface SettingsArgs {
  schoolId: string;
}

export interface UpdateSettingsArgs {
  schoolId: string;
  sendDelayMinutes: number;
}

export interface UpsertPreferencesArgs {
  profileId: string;
  schoolId: string;
  prefs: Partial<Record<string, unknown>>;
}

export interface SaveSubscriptionArgs {
  profileId: string;
  schoolId: string;
  endpoint: string;
  p256dh: string;
  auth_key: string;
  user_agent: string | null;
}

export interface RemoveSubscriptionArgs {
  subscriptionId: string;
  profileId: string;
}

export interface PreviewArgs {
  date: string;
  teacherId?: string;
}

// ─── API Slice ────────────────────────────────────────────────────────────────

export const notificationsApi = createApi({
  reducerPath: "notificationsApi",
  baseQuery: fakeBaseQuery(),
  tagTypes: [
    "Preferences",
    "Subscriptions",
    "QueueStatus",
    "Settings",
    "DailyDeliveries",
    "NotificationHistory",
    // Per-date preview cache (eligibility, diff, recipients). Invalidated
    // by every action that can change the underlying substitution state
    // on a date — publish/unpublish, send_now, cancel, retry — so the
    // banner count stays in sync with what's actually published.
    "NotificationPreview",
  ],
  endpoints: () => ({}), // Injected from separate files
});
